import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Route, BookOpen, CheckCircle2, Clock, ChevronRight, Loader2, ArrowLeft,
  Users, BarChart3, AlertTriangle, Sparkles, TrendingUp, Target, Search,
  ChevronDown, Play, Lock, Award, Zap, Filter, RefreshCw, Brain,
  Monitor, Presentation, ArrowRight, UserPlus
} from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { AdminLayout } from "../../components/layout/AdminLayout";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadialBarChart, RadialBar
} from "recharts";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];
const PIE_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#94a3b8"];

const SEVERITY_STYLE = {
  critical: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-500" },
  warning: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500" },
  success: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
  info: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", dot: "bg-blue-500" },
};

const LearningPathDetailPage = () => {
  const { pathId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [learnerSearch, setLearnerSearch] = useState("");
  const [learnerFilter, setLearnerFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("path");

  const variant = user?.role === "manager" ? "manager" : user?.role === "tenant_admin" ? "tenant" : "admin";
  const basePath = user?.role === "manager" ? "/manager" : user?.role === "tenant_admin" ? "/tenant" : "/admin";

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/learning-paths/${pathId}/analytics`);
      setAnalytics(res.data);
    } catch (e) {
      toast.error("Failed to load analytics");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [pathId]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const generateAiInsights = async () => {
    if (!analytics) return;
    setAiLoading(true);
    try {
      const res = await api.post(`/learning-paths/${pathId}/ai-insights`, {
        analytics_summary: {
          path_title: analytics.path.title,
          total_learners: analytics.summary.total_learners,
          completion_rate: analytics.summary.completion_rate,
          avg_progress: analytics.summary.avg_progress,
          bottleneck_count: analytics.summary.bottleneck_count,
          course_summary: analytics.course_analytics.map(c => ({
            title: c.title, completion_rate: c.completion_rate,
            avg_score: c.avg_score, is_bottleneck: c.is_bottleneck,
          })),
          department_breakdown: analytics.department_breakdown,
        }
      });
      setAiInsights(res.data);
    } catch (e) {
      toast.error("Failed to generate AI insights");
    } finally {
      setAiLoading(false);
    }
  };

  if (loading || !analytics) {
    return (
      <AdminLayout title="Learning Path Analytics" variant={variant}>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  const { path, summary, course_analytics, learner_analytics, department_breakdown } = analytics;

  const filteredLearners = learner_analytics.filter(l => {
    if (learnerSearch && !l.full_name?.toLowerCase().includes(learnerSearch.toLowerCase()) && !l.email?.toLowerCase().includes(learnerSearch.toLowerCase())) return false;
    if (learnerFilter !== "all" && l.status !== learnerFilter) return false;
    return true;
  });

  const statusPieData = [
    { name: "Completed", value: summary.completed, color: "#10b981" },
    { name: "In Progress", value: summary.in_progress, color: "#3b82f6" },
    { name: "Not Started", value: summary.not_started, color: "#94a3b8" },
  ].filter(d => d.value > 0);

  const courseBarData = course_analytics.map(c => ({
    name: c.title.length > 20 ? c.title.slice(0, 20) + "..." : c.title,
    fullName: c.title,
    completed: c.completed,
    in_progress: c.in_progress,
    not_started: c.not_started,
    completion_rate: c.completion_rate,
  }));

  const tabs = [
    { id: "path", label: "Path", icon: Route },
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "courses", label: "Courses", icon: BookOpen },
    { id: "learners", label: "Learners", icon: Users },
    { id: "ai", label: "AI Insights", icon: Brain },
  ];

  return (
    <AdminLayout title={path.title} variant={variant}>
      <div className="space-y-6" data-testid="learning-path-detail">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Button variant="ghost" size="icon" className="mt-1 shrink-0" onClick={() => navigate(`${basePath}/learning-paths`)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="text-[10px]">
                  <Route className="w-3 h-3 mr-1" /> Learning Path
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {path.course_count} courses
                </Badge>
                {path.estimated_hours > 0 && (
                  <Badge variant="outline" className="text-[10px]">
                    <Clock className="w-3 h-3 mr-1" /> {path.estimated_hours}h
                  </Badge>
                )}
              </div>
              <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Sora', sans-serif" }}>
                {path.title}
              </h1>
              {path.description && (
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{path.description}</p>
              )}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAnalytics} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        {/* Summary Stats - HubSpot style */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label: "Total Learners", value: summary.total_learners, icon: Users, color: "#3b82f6", bg: "rgba(59,130,246,0.08)" },
            { label: "Completion Rate", value: `${summary.completion_rate}%`, icon: Target, color: "#10b981", bg: "rgba(16,185,129,0.08)" },
            { label: "Avg Progress", value: `${summary.avg_progress}%`, icon: TrendingUp, color: "#8b5cf6", bg: "rgba(139,92,246,0.08)" },
            { label: "Completed", value: summary.completed, icon: CheckCircle2, color: "#10b981", bg: "rgba(16,185,129,0.08)" },
            { label: "Bottlenecks", value: summary.bottleneck_count, icon: AlertTriangle, color: summary.bottleneck_count > 0 ? "#ef4444" : "#94a3b8", bg: summary.bottleneck_count > 0 ? "rgba(239,68,68,0.08)" : "rgba(148,163,184,0.08)" },
          ].map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Card className="border hover:shadow-md transition-shadow" data-testid={`summary-stat-${i}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: s.bg }}>
                      <s.icon className="w-4.5 h-4.5" style={{ color: s.color }} />
                    </div>
                  </div>
                  <p className="text-2xl font-bold tracking-tight">{s.value}</p>
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-muted/50 rounded-xl w-fit" data-testid="analytics-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === "path" && (
            <motion.div key="path" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
              {/* Visual Roadmap */}
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
                    <Route className="w-4 h-4 text-primary" /> Learning Path Roadmap
                  </h3>
                  <div className="relative">
                    <div className="flex items-start gap-0 overflow-x-auto py-4 px-2 pb-6 scrollbar-thin">
                      {course_analytics.map((course, i) => {
                        const isILT = course.course_type === "ilt";
                        const completionColor = course.completion_rate >= 70 ? "border-emerald-400 bg-emerald-50" : course.completion_rate >= 30 ? "border-blue-400 bg-blue-50" : isILT ? "border-amber-300 bg-amber-50" : "border-slate-300 bg-slate-50";
                        return (
                          <div key={course.course_id} className="flex items-start shrink-0">
                            <div className="relative flex flex-col items-center group">
                              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center border-2 transition-all ${completionColor} group-hover:shadow-lg`}>
                                {isILT ? <Presentation className="w-7 h-7 text-amber-600" /> : <Monitor className="w-7 h-7 text-blue-600" />}
                                {course.completion_rate === 100 && (
                                  <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                                  </div>
                                )}
                                {course.is_bottleneck && (
                                  <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                                    <AlertTriangle className="w-3 h-3 text-white" />
                                  </div>
                                )}
                              </div>
                              <div className="mt-2 text-center w-[130px]">
                                <p className="text-[11px] font-semibold leading-tight line-clamp-2" title={course.title}>{course.title}</p>
                                <div className="flex items-center justify-center gap-1 mt-1">
                                  <Badge variant="outline" className={`text-[8px] px-1.5 py-0 h-4 ${isILT ? "text-amber-600 border-amber-200" : "text-blue-600 border-blue-200"}`}>
                                    {isILT ? "ILT" : "eLearning"}
                                  </Badge>
                                  {course.duration_hours > 0 && (
                                    <span className="text-[9px] text-muted-foreground">{course.duration_hours}h</span>
                                  )}
                                </div>
                                {/* Mini completion bar */}
                                <div className="w-full mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full transition-all" style={{ width: `${course.completion_rate}%`, background: course.completion_rate >= 70 ? "#10b981" : course.completion_rate >= 30 ? "#3b82f6" : "#94a3b8" }} />
                                </div>
                                <p className="text-[9px] text-muted-foreground mt-0.5">{course.completion_rate}% complete</p>
                              </div>
                            </div>
                            {i < course_analytics.length - 1 && (
                              <div className="flex items-center mx-3 mt-5">
                                <div className="w-12 h-0.5 bg-slate-200 relative">
                                  <ArrowRight className="w-4 h-4 text-slate-300 absolute -right-2 -top-[7px]" />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <div className="shrink-0 w-6" />
                    </div>
                  </div>
                  {/* Legend */}
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-muted text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1"><Monitor className="w-3 h-3 text-blue-500" /> eLearning</span>
                    <span className="flex items-center gap-1"><Presentation className="w-3 h-3 text-amber-500" /> ILT</span>
                    <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> 100% completed</span>
                    <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3 text-red-500" /> Bottleneck</span>
                  </div>
                </CardContent>
              </Card>

              {/* Course List in Path Order */}
              <div className="space-y-3">
                {course_analytics.map((course, i) => {
                  const isILT = course.course_type === "ilt";
                  return (
                    <Card key={course.course_id} className={`${course.is_bottleneck ? "border-red-200" : ""}`} data-testid={`path-course-${i}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">{i + 1}</span>
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${isILT ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200"}`}>
                              {isILT ? <Presentation className="w-5 h-5 text-amber-600" /> : <Monitor className="w-5 h-5 text-blue-600" />}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold">{course.title}</h4>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              <span>{course.category}</span>
                              {course.duration_hours > 0 && <span>{course.duration_hours}h</span>}
                              <Badge variant="outline" className="text-[9px]">{isILT ? "ILT" : "eLearning"}</Badge>
                              {course.is_bottleneck && <Badge variant="destructive" className="text-[9px]"><AlertTriangle className="w-3 h-3 mr-0.5" /> Bottleneck</Badge>}
                            </div>
                          </div>
                          <div className="flex items-center gap-6 shrink-0 text-center">
                            <div>
                              <p className="text-lg font-bold text-emerald-600">{course.completed}</p>
                              <p className="text-[10px] text-muted-foreground">Completed</p>
                            </div>
                            <div>
                              <p className="text-lg font-bold text-blue-600">{course.in_progress}</p>
                              <p className="text-[10px] text-muted-foreground">In Progress</p>
                            </div>
                            <div className="w-14">
                              <div className="relative w-12 h-12 mx-auto">
                                <svg className="w-12 h-12 -rotate-90" viewBox="0 0 36 36">
                                  <path d="M18 2.5a15.5 15.5 0 010 31 15.5 15.5 0 010-31" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                                  <path d="M18 2.5a15.5 15.5 0 010 31 15.5 15.5 0 010-31" fill="none" stroke={course.completion_rate >= 70 ? "#10b981" : course.completion_rate >= 30 ? "#3b82f6" : "#94a3b8"} strokeWidth="3" strokeDasharray={`${course.completion_rate} ${100 - course.completion_rate}`} strokeLinecap="round" />
                                </svg>
                                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold">{course.completion_rate}%</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </motion.div>
          )}

          {activeTab === "overview" && (
            <motion.div key="overview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Learner Status Pie */}
                <Card>
                  <CardContent className="p-5">
                    <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
                      <Users className="w-4 h-4 text-primary" /> Learner Distribution
                    </h3>
                    <div className="flex items-center gap-6">
                      <ResponsiveContainer width={160} height={160}>
                        <PieChart>
                          <Pie data={statusPieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3} strokeWidth={0}>
                            {statusPieData.map((entry, i) => (
                              <Cell key={i} fill={entry.color} />
                            ))}
                          </Pie>
                          <RTooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-3 flex-1">
                        {statusPieData.map((d, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full" style={{ background: d.color }} />
                              <span className="text-sm text-muted-foreground">{d.name}</span>
                            </div>
                            <span className="text-sm font-bold">{d.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Department Performance */}
                <Card>
                  <CardContent className="p-5">
                    <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4 text-primary" /> Department Progress
                    </h3>
                    <div className="space-y-3">
                      {department_breakdown.map((dept, i) => (
                        <div key={i} className="space-y-1.5">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium">{dept.department}</span>
                            <span className="text-muted-foreground">{dept.avg_progress}% avg</span>
                          </div>
                          <div className="flex gap-1 h-2 rounded-full overflow-hidden bg-muted/50">
                            <div className="bg-emerald-500 rounded-full transition-all" style={{ width: `${(dept.completed / Math.max(dept.total, 1)) * 100}%` }} />
                            <div className="bg-blue-500 rounded-full transition-all" style={{ width: `${(dept.in_progress / Math.max(dept.total, 1)) * 100}%` }} />
                          </div>
                          <div className="flex gap-3 text-[10px] text-muted-foreground">
                            <span>{dept.completed} completed</span>
                            <span>{dept.in_progress} in progress</span>
                            <span>{dept.not_started} not started</span>
                          </div>
                        </div>
                      ))}
                      {department_breakdown.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-6">No department data</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Course Completion Bar Chart */}
              <Card>
                <CardContent className="p-5">
                  <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-primary" /> Course Completion Breakdown
                  </h3>
                  <ResponsiveContainer width="100%" height={Math.max(200, courseBarData.length * 50)}>
                    <BarChart data={courseBarData} layout="vertical" margin={{ left: 10, right: 20 }}>
                      <XAxis type="number" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                      <RTooltip
                        contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                        formatter={(value, name) => [value, name === "completed" ? "Completed" : name === "in_progress" ? "In Progress" : "Not Started"]}
                      />
                      <Bar dataKey="completed" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="in_progress" stackId="a" fill="#3b82f6" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="not_started" stackId="a" fill="#e2e8f0" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeTab === "courses" && (
            <motion.div key="courses" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
              {course_analytics.map((course, i) => (
                <motion.div key={course.course_id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                  <Card className={`transition-all ${course.is_bottleneck ? "border-red-200 bg-red-50/20" : ""}`} data-testid={`course-card-${course.course_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-sm">{course.title}</h4>
                            {course.is_bottleneck && (
                              <Badge variant="destructive" className="text-[10px] h-5">
                                <AlertTriangle className="w-3 h-3 mr-0.5" /> Bottleneck
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-[10px]">{course.course_type}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">{course.category} {course.duration_hours > 0 ? `  ${course.duration_hours}h` : ""}</p>

                          <div className="mt-3 grid grid-cols-5 gap-4">
                            {[
                              { label: "Assigned", value: course.total_assigned },
                              { label: "Enrolled", value: course.enrolled },
                              { label: "Completed", value: course.completed, color: "text-emerald-600" },
                              { label: "In Progress", value: course.in_progress, color: "text-blue-600" },
                              { label: "Avg Score", value: course.avg_score > 0 ? `${course.avg_score}%` : "-" },
                            ].map((m, j) => (
                              <div key={j}>
                                <p className={`text-lg font-bold ${m.color || ""}`}>{m.value}</p>
                                <p className="text-[10px] text-muted-foreground">{m.label}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="w-20 text-center shrink-0">
                          <div className="relative w-16 h-16 mx-auto">
                            <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                              <path d="M18 2.5a15.5 15.5 0 010 31 15.5 15.5 0 010-31" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                              <path d="M18 2.5a15.5 15.5 0 010 31 15.5 15.5 0 010-31" fill="none" stroke={course.completion_rate >= 70 ? "#10b981" : course.completion_rate >= 40 ? "#3b82f6" : "#ef4444"} strokeWidth="3" strokeDasharray={`${course.completion_rate} ${100 - course.completion_rate}`} strokeLinecap="round" />
                            </svg>
                            <span className="absolute inset-0 flex items-center justify-center text-sm font-bold">{course.completion_rate}%</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-1">Completion</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          )}

          {activeTab === "learners" && (
            <motion.div key="learners" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
              {/* Filters */}
              <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="Search learners..." value={learnerSearch} onChange={e => setLearnerSearch(e.target.value)} className="pl-10 h-9" data-testid="learner-search" />
                </div>
                <Select value={learnerFilter} onValueChange={setLearnerFilter}>
                  <SelectTrigger className="w-[160px] h-9" data-testid="learner-filter">
                    <Filter className="w-3 h-3 mr-1" /><SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All ({learner_analytics.length})</SelectItem>
                    <SelectItem value="completed">Completed ({summary.completed})</SelectItem>
                    <SelectItem value="in_progress">In Progress ({summary.in_progress})</SelectItem>
                    <SelectItem value="not_started">Not Started ({summary.not_started})</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Learner Table */}
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-muted">
                          <th className="text-left p-3 font-semibold text-muted-foreground">Learner</th>
                          <th className="text-left p-3 font-semibold text-muted-foreground">Department</th>
                          <th className="text-center p-3 font-semibold text-muted-foreground">Progress</th>
                          <th className="text-center p-3 font-semibold text-muted-foreground">Courses</th>
                          <th className="text-center p-3 font-semibold text-muted-foreground">Avg Score</th>
                          <th className="text-center p-3 font-semibold text-muted-foreground">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLearners.map((l, i) => (
                          <tr key={l.user_id} className="border-b border-muted/50 hover:bg-muted/30 transition-colors" data-testid={`learner-row-${i}`}>
                            <td className="p-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                                  {l.full_name?.split(" ").map(n => n[0]).join("").slice(0, 2)}
                                </div>
                                <div>
                                  <p className="font-medium">{l.full_name}</p>
                                  <p className="text-xs text-muted-foreground">{l.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-3 text-muted-foreground">{l.department || "-"}</td>
                            <td className="p-3">
                              <div className="flex items-center gap-2 justify-center">
                                <Progress value={l.progress_percent} className="w-16 h-1.5" />
                                <span className="text-xs font-bold w-8 text-right">{l.progress_percent}%</span>
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <span className="font-bold text-emerald-600">{l.completed_courses}</span>
                              <span className="text-muted-foreground">/{l.total_courses}</span>
                            </td>
                            <td className="p-3 text-center font-medium">{l.avg_score > 0 ? `${l.avg_score}%` : "-"}</td>
                            <td className="p-3 text-center">
                              <Badge variant="outline" className={`text-[10px] ${
                                l.status === "completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                l.status === "in_progress" ? "bg-blue-50 text-blue-700 border-blue-200" :
                                "bg-slate-50 text-slate-600 border-slate-200"
                              }`}>
                                {l.status === "completed" ? "Completed" : l.status === "in_progress" ? "In Progress" : "Not Started"}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredLearners.length === 0 && (
                      <div className="py-12 text-center text-muted-foreground">No learners match your filter</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeTab === "ai" && (
            <motion.div key="ai" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
              {/* Generate button */}
              {!aiInsights && !aiLoading && (
                <Card className="border-dashed border-2 border-primary/20">
                  <CardContent className="py-12 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Brain className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold mb-2">AI-Powered Analytics</h3>
                    <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                      Generate intelligent insights about learner performance, bottleneck detection, and actionable recommendations.
                    </p>
                    <Button onClick={generateAiInsights} size="lg" data-testid="generate-ai-btn">
                      <Sparkles className="w-5 h-5 mr-2" /> Generate AI Insights
                    </Button>
                  </CardContent>
                </Card>
              )}

              {aiLoading && (
                <Card>
                  <CardContent className="py-16 text-center">
                    <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
                    <p className="text-sm text-muted-foreground">Analyzing learning path data with AI...</p>
                  </CardContent>
                </Card>
              )}

              {aiInsights && !aiLoading && (
                <div className="space-y-6">
                  {/* Executive Summary */}
                  <Card className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white border-0">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Brain className="w-5 h-5 text-primary" />
                        <h3 className="text-sm font-bold text-white/70 uppercase tracking-wider">AI Executive Summary</h3>
                      </div>
                      <p className="text-base leading-relaxed text-white/90">{aiInsights.summary}</p>
                      <Button variant="outline" size="sm" className="mt-4 text-white border-white/20 hover:bg-white/10" onClick={generateAiInsights}>
                        <RefreshCw className="w-4 h-4 mr-1" /> Regenerate
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Insights Grid */}
                  {aiInsights.insights?.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {aiInsights.insights.map((insight, i) => {
                        const style = SEVERITY_STYLE[insight.severity] || SEVERITY_STYLE.info;
                        return (
                          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
                            <Card className={`${style.bg} ${style.border} border h-full`} data-testid={`ai-insight-${i}`}>
                              <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                  <span className={`w-2.5 h-2.5 rounded-full ${style.dot} mt-1.5 shrink-0`} />
                                  <div>
                                    <h4 className={`text-sm font-bold ${style.text}`}>{insight.title}</h4>
                                    <p className="text-xs text-muted-foreground mt-1">{insight.description}</p>
                                    {insight.metric && (
                                      <span className={`inline-block mt-2 text-xs font-bold px-2 py-0.5 rounded-full ${style.bg} ${style.text}`}>
                                        {insight.metric}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}

                  {/* Recommendations */}
                  {aiInsights.recommendations?.length > 0 && (
                    <Card>
                      <CardContent className="p-5">
                        <h3 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
                          <Zap className="w-4 h-4 text-amber-500" /> AI Recommendations
                        </h3>
                        <div className="space-y-3">
                          {aiInsights.recommendations.map((rec, i) => (
                            <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-amber-50/50 border border-amber-100">
                              <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700 shrink-0 mt-0.5">{i + 1}</span>
                              <p className="text-sm text-slate-700">{rec}</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AdminLayout>
  );
};

export default LearningPathDetailPage;
